import { useEffect, useState } from 'react';
import { Button, Checkbox, Collapse, Pagination } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@hooks/redux';
import { transactionMassiveApproveApi } from '@http/transactions';
import getStatus from '@utils/getStatus';
import digitNumber from '@utils/digitNumber';
import TransactionInfo from './TransactionInfo';
import TransactionActionModal from './TransactionActionModal';
import { PAGE_SIZE } from './constants';
import { Transaction, TransactionAction, TransactionListResponse } from './types';

const GRID_COLUMNS =
  'grid gap-3 md:gap-5 ' +
  '[grid-template-columns:repeat(12,minmax(0,1fr))_30px] ' +
  'sm:[grid-template-columns:repeat(2,minmax(38px,1fr))_repeat(8,minmax(0,1fr))_repeat(2,minmax(48px,1fr))_30px] ' +
  'lg:[grid-template-columns:80px_repeat(2,minmax(75px,1fr))_repeat(3,minmax(50px,1fr))_repeat(2,minmax(0,1fr))_repeat(2,minmax(50px,1fr))_repeat(2,minmax(60px,1fr))_30px]';

interface TransactionListProps {
  data: TransactionListResponse;
  page: number;
  status: string;
  onPageChange: (page: number) => void;
  onReload: () => void;
}

function TransactionList({ data, page, status, onPageChange, onReload }: TransactionListProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedAction, setSelectedAction] = useState<TransactionAction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const transactions = data?.results ?? [];

  useEffect(() => {
    setActiveKeys([]);
    setSelectedIds([]);
  }, [data]);

  const toggleSelected = (id: string, isChecked: boolean) => {
    setSelectedIds(prev => (isChecked ? [...prev, id] : prev.filter(selectedId => selectedId !== id)));
  };

  const submitSelectedAction = async () => {
    if (!selectedAction || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    await dispatch(transactionMassiveApproveApi({ trans_id: selectedIds, action: selectedAction }));
    setIsSubmitting(false);
    setSelectedAction(null);
    onReload();
  };

  const renderRow = (item: Transaction) => (
    <div className={`${GRID_COLUMNS} sm:items-center py-5 cursor-pointer`}>
      <p className='lg:col-span-1 md:col-span-2 max-sm:hidden col-span-2'>{item.created_at.slice(0, 10)}</p>
      <p className='lg:col-span-2 md:col-span-3 col-span-3 break-all max-sm:hidden'>{item.sending_ac}</p>
      <p className='lg:col-span-3 md:col-span-3 col-span-3 break-all max-sm:hidden'>
        {item.type === 'ELECTRIC' ? item.utilpay_account : item.receiving_ac}
      </p>
      <div className='lg:hidden col-span-2 max-sm:col-span-6'>
        <p className='max-sm:font-semibold'>{item.type_name}</p>
        <p className='sm:hidden text-slate-600'>{item.sending_ac}</p>
        <p className='md:hidden text-slate-600'>{item.created_at.slice(0, 10)}</p>
      </div>
      <p className='lg:col-span-2 max-lg:hidden'>{item.type_name}</p>
      <p className='col-span-2 text-right max-lg:hidden'>{getStatus(item.status, 12, t)}</p>
      <p className='col-span-2 text-right max-lg:hidden'>
        {digitNumber(item.sending_amount)} {item.sending_ccy}
      </p>
      <div className='col-span-2 text-right max-sm:col-span-6 lg:hidden'>
        <p>
          {digitNumber(item.sending_amount)} {item.sending_ccy}
        </p>
        <p>{getStatus(item.status, 12, t)}</p>
      </div>
      {status === 'TO_APPROVE' && item.is_authenticator && (
        <div className='col-span-1 flex justify-end'>
          <Checkbox
            checked={selectedIds.includes(item.trans_id)}
            onClick={e => e.stopPropagation()}
            onChange={e => toggleSelected(item.trans_id, e.target.checked)}
          />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div
        className={`${GRID_COLUMNS} tableHeader border-b py-5 pl-[34px] pr-[10px] min-[520px]:pl-[44px] min-[520px]:pr-5`}
      >
        <p className='lg:col-span-1 md:col-span-2 col-span-2 max-sm:hidden'>{t('Date')}</p>
        <p className='lg:col-span-2 md:col-span-3 col-span-3 max-sm:hidden'>{t('Sender')}</p>
        <p className='lg:col-span-3 md:col-span-3 col-span-3 max-sm:hidden'>{t('Recipient')}</p>
        <p className='lg:col-span-2 md:col-span-2 col-span-2 max-sm:col-span-6'>{t('Transaction type')}</p>
        <p className='col-span-2 text-right max-lg:hidden'>{t('Status')}</p>
        <p className='col-span-2 text-right max-sm:col-span-6'>{t('Amount')}</p>
      </div>

      {transactions.length > 0 ? (
        <div>
          <Collapse
            ghost
            className='p-0'
            activeKey={activeKeys}
            onChange={keys => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
            items={transactions.map(item => ({
              key: item.trans_id,
              label: renderRow(item),
              children: <TransactionInfo item={item} onReload={onReload} />,
            }))}
          />
          <div className='mt-7 flex justify-between'>
            <Pagination
              current={page}
              pageSize={PAGE_SIZE}
              total={(data.total_pages ?? 0) * PAGE_SIZE}
              showSizeChanger={false}
              onChange={onPageChange}
            />
            {selectedIds.length > 0 && (
              <div>
                <Button type='primary' danger className='mr-4' onClick={() => setSelectedAction('DECLINE')}>
                  {t('Reject')}
                </Button>
                <Button type='primary' onClick={() => setSelectedAction('APPROVE')}>
                  {t('Confirm')}
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className='h-24 lg:h-72 flex items-center justify-center'>
          <p>{t('No Transactions')}</p>
        </div>
      )}

      <TransactionActionModal
        action={selectedAction}
        isSubmitting={isSubmitting}
        onConfirm={submitSelectedAction}
        onCancel={() => setSelectedAction(null)}
      />
    </div>
  );
}

export default TransactionList;
